import PropTypes from "prop-types";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import { useForm, Controller } from "react-hook-form";
import {
  AppBar,
  Toolbar,
  IconButton,
  Autocomplete,
  TextField,
  FormControl,
  Box,
  Checkbox,
} from "@mui/material";
import { bgBlur } from "../../../utils/cssStyles";
import useOffSetTop from "../../../hooks/useOffSetTop";
import useResponsive from "../../../hooks/useResponsive";
import { HEADER, NAV } from "../../../config-global";
import Logo from "../../../components/logo";
import Iconify from "../../../components/iconify";
import { useSettingsContext } from "../../../components/settings";
import AccountPopover from "./AccountPopover";
import { useLocation } from "react-router-dom";
import { getAssociatedVenuesList } from "src/redux/slices/venue";
import { useDispatch, useSelector } from "react-redux";
import { useAuthContext } from "src/auth/useAuthContext";
import {
  setSelectedVenue,
  setSelectedSeriesData,
} from "src/redux/slices/stats";
import { Stack } from "@mui/system";

Header.propTypes = {
  onOpenNav: PropTypes.func,
};

const ALL_VENUES_OPTION = "All";

const isAllOption = (option) => option === ALL_VENUES_OPTION;

const getVenueId = (venue) => venue?.id || venue?._id;

const getVenueOptionLabel = (option) =>
  typeof option === "string" ? option : option?.name || "";

const isSameVenueOption = (option, value) => {
  if (typeof option === "string" || typeof value === "string") {
    return option === value;
  }

  return getVenueId(option) === getVenueId(value);
};

const normalizeSelectedVenueValue = (selectedVenue) => {
  if (!selectedVenue || selectedVenue === ALL_VENUES_OPTION) {
    return [ALL_VENUES_OPTION];
  }

  return Array.isArray(selectedVenue) ? selectedVenue : [selectedVenue];
};

const getSelectedVenuePayload = (selectedOptions, changedOption, reason) => {
  if (reason === "selectOption" && isAllOption(changedOption)) {
    return ALL_VENUES_OPTION;
  }

  const venues = selectedOptions.filter((option) => !isAllOption(option));

  if (!venues.length) {
    return ALL_VENUES_OPTION;
  }

  return venues.length === 1 ? venues[0] : venues;
};

export default function Header({ onOpenNav }) {
  const theme = useTheme();
  const { themeLayout } = useSettingsContext();
  const isNavHorizontal = themeLayout === "horizontal";
  const isNavMini = themeLayout === "mini";
  const isDesktop = useResponsive("up", "lg");
  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const { franchiseId } = user ?? {};
  const dispatch = useDispatch();
  const { associatedVenues } = useSelector((state) => state?.venue);
  const { selectedVenue } = useSelector((state) => state?.stat);
  const NewUserSchema = Yup.object().shape({ venueSearch: Yup.mixed() });

  const defaultValues = useMemo(
    () => ({ venueSearch: [ALL_VENUES_OPTION] }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    control,
    formState: { errors },
  } = methods;

  const isDashboard =
    pathname == "/dashboard/app" ||
    pathname == "/dashboard/customer/list" ||
    pathname == "/dashboard/report"
      ? true
      : false;

  const isMultiVenueFilter = pathname === "/dashboard/app";

  const venueOptions = useMemo(
    () => [ALL_VENUES_OPTION, ...(associatedVenues || [])],
    [associatedVenues]
  );

  const selectedVenueValue = useMemo(
    () => normalizeSelectedVenueValue(selectedVenue),
    [selectedVenue]
  );

  const handleVenueSelect = useCallback(
    (selectedVenue) => {
      dispatch(setSelectedVenue(selectedVenue));
      dispatch(setSelectedSeriesData("Today"));
    },
    [dispatch]
  );

  useEffect(() => {
    if (franchiseId?.id) {
      dispatch(getAssociatedVenuesList(franchiseId.id));
    }
  }, [dispatch, franchiseId]);

  useEffect(() => {
    if (!isMultiVenueFilter && Array.isArray(selectedVenue)) {
      handleVenueSelect(ALL_VENUES_OPTION);
    }
  }, [handleVenueSelect, isMultiVenueFilter, selectedVenue]);

  const renderVenueFilter = () => {
    if (!isMultiVenueFilter) {
      const singleValue = Array.isArray(selectedVenue)
        ? ALL_VENUES_OPTION
        : selectedVenue || ALL_VENUES_OPTION;

      return (
        <Autocomplete
          options={venueOptions}
          getOptionLabel={getVenueOptionLabel}
          isOptionEqualToValue={isSameVenueOption}
          value={singleValue}
          onChange={(event, newValue) => {
            const value = newValue || ALL_VENUES_OPTION;
            handleVenueSelect(value);
          }}
          onBlur={(event) => {
            if (!event.target.value) {
              handleVenueSelect(ALL_VENUES_OPTION);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search by venue name"
              variant="outlined"
              error={!!errors.venueSearch}
            />
          )}
        />
      );
    }

    return (
      <Controller
        name="venueSearch"
        control={control}
        render={({ field }) => (
          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={2}
            options={venueOptions}
            getOptionLabel={getVenueOptionLabel}
            isOptionEqualToValue={isSameVenueOption}
            value={selectedVenueValue}
            onChange={(event, newValue, reason, details) => {
              const value = getSelectedVenuePayload(
                newValue,
                details?.option,
                reason
              );

              field.onChange(normalizeSelectedVenueValue(value));
              handleVenueSelect(value);
            }}
            onBlur={(event) => {
              field.onBlur();

              if (!event.target.value && !selectedVenueValue.length) {
                handleVenueSelect(ALL_VENUES_OPTION);
              }
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                {getVenueOptionLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by venue name"
                variant="outlined"
                error={!!errors.venueSearch}
              />
            )}
          />
        )}
      />
    );
  };

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}
      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: "text.primary" }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {isDashboard && (
        <Box
          sx={{
            ml: "auto",
            width: {
              xs: "100%",
              sm: "100%",
              md: "75%",
              lg: "50%",
            },
          }}
          mr={2}
        >
          <FormControl fullWidth error={!!errors.venueSearch}>
            {renderVenueFilter()}
          </FormControl>
        </Box>
      )}

      <Stack
        flexGrow={isDashboard ? "" : 1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
      >
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: "none",
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(["height"], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: "background.default",
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}